import { OnboardingSaveHighwaysDto, DayOfWeek } from '@interfaces/models';
import { HighwaySectionDto } from '@interfaces/types';
import { useEffect, useState } from 'react';

import { useServicesFormatter } from '../hooks';

export const useHighwaysConfiguration = ({
  initialConfiguration,
  defaultSelected,
  highwaySections,
  onUpdate,
  days,
}: {
  onUpdate: (config: OnboardingSaveHighwaysDto | null) => void;
  initialConfiguration?: OnboardingSaveHighwaysDto | null;
  highwaySections: HighwaySectionDto[];
  defaultSelected?: boolean;
  days: DayOfWeek[];
}) => {
  const [isEntityActive, setIsEntityActive] = useState(!!initialConfiguration);
  const [isEditing, setIsEditing] = useState(false);
  const [configuration, setConfiguration] = useState<OnboardingSaveHighwaysDto | null>(
    initialConfiguration || null,
  );

  useEffect(() => {
    setConfiguration(initialConfiguration || null);
  }, [initialConfiguration]);

  useEffect(() => {
    if (defaultSelected) handleActiveChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected]);

  const { formatDays, formatHighwaysSections } = useServicesFormatter();

  const prepareHighwaysPreviewData = (highwaysConfiguration: OnboardingSaveHighwaysDto) => {
    return [
      {
        key: 'COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SECTIONS',
        value: formatHighwaysSections(highwaysConfiguration.highwaySections, highwaySections),
      },
      {
        key: 'COMPANY_POLICY.HIGHWAYS_CONFIGURATION_DAYS',
        value: formatDays(highwaysConfiguration.days as DayOfWeek[]),
      },
    ];
  };

  const createDefaultConfiguration = (): OnboardingSaveHighwaysDto => ({
    highwaySections: highwaySections.map((section) => section.code),
    days: days.slice(0, 5),
  });

  const handleActiveChange = (active: boolean) => {
    if (active) {
      const defaultConfig = createDefaultConfiguration();
      setIsEntityActive(true);
      setConfiguration(defaultConfig);
      onUpdate(defaultConfig);
    } else {
      setIsEntityActive(false);
      setConfiguration(null);
      setIsEditing(false);
      onUpdate(null);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = (newConfig: OnboardingSaveHighwaysDto) => {
    setConfiguration(newConfig);
    setIsEditing(false);
    onUpdate(newConfig);
  };

  const handleRevertChanges = () => {
    const defaultConfig = createDefaultConfiguration();
    setConfiguration(defaultConfig);
    setIsEditing(false);
    onUpdate(defaultConfig);
  };

  return {
    isEntityActive,
    isEditing,
    configuration,
    prepareHighwaysPreviewData,
    handleActiveChange,
    handleEdit,
    handleSave,
    handleRevertChanges,
    createDefaultConfiguration,
  };
};
